import React from 'react';
import { observer } from 'mobx-react';
import { mutate } from 'swr';
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ModalStep } from '@components/common/multiStepModal';
import { LightFormBuilder } from '@components/common/lightFormBuilder';
import { ApiGetResource, updateNonOauthAccountApi } from '@api';
import useStores from '@stores/useStores';
import { useMultiStepModalStore } from '@components/common/multiStepModal/modalStore';

import { useDesignVariant } from '../../designVariantContext';
import { useAccountManagementStore } from '../../store';
import { getResourceCacheKey } from '../../hooks/useLoadAccountsIntoAccountManagementStore';

const MASKED_PLACEHOLDER = '**********';

export const AccountEditFormModalStep: React.FC = observer(() => {
  const variant = useDesignVariant();
  const isNext = variant === 'next';

  const { workspaceStore, messagesStore } = useStores();
  const accountManagementStore = useAccountManagementStore();
  const multiStepModalStore = useMultiStepModalStore();
  const {
    selectedEditAccountId,
    compatibleAccountDefinitions,
    form,
    getEditNonOAuthPayload,
    resourceDefinitionName,
    findAccountById,
  } = accountManagementStore;

  const account = findAccountById(selectedEditAccountId);
  const accountDefinition = compatibleAccountDefinitions.find(
    (def) => def.name === account?.accountDefinitionName,
  );

  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!account || !accountDefinition) {
      return;
    }

    // Pre-populate non-secret option fields from the account's existing values.
    // Secret fields are intentionally left empty so the user must re-enter them if desired.
    const optionKeys = new Set(accountDefinition.config.optionFields);
    const optionsValues = Object.fromEntries(
      Object.entries(account.options).filter(([k, v]) => optionKeys.has(k) && v != null),
    );

    form.setFieldsValue({ name: account.name, options: optionsValues });
  }, [account, accountDefinition, form]);

  if (!accountDefinition || !account) {
    return null;
  }

  // Derive the edit-flow form config from the ui-config, which is
  // primarily written for the create flow and requires adjustments to handle
  // secrets correctly in the edit flow.
  const editFormConfig = {
    ...accountDefinition.uiConfig.form,
    fields: accountDefinition.uiConfig.form.fields.map((fieldConfig) => {
      const { key } = fieldConfig;
      // key can be a nested path array (e.g. ["options", "apiKey"]) or a plain string (e.g. "name")
      const fieldName = Array.isArray(key) ? key[key.length - 1] : key;
      if (account.secret?.[fieldName] != null) {
        // Secret fields are left empty in the form but already have a value
        // stored in the backend. Mark them as optional so the user isn't
        // forced to re-enter them and form validation doesn't fail.
        return { ...fieldConfig, placeholder: MASKED_PLACEHOLDER, optional: true };
      }
      return fieldConfig;
    }),
  };

  return (
    <ModalStep
      gap="16px"
      header={{
        title: 'Edit account',
        description: `Update your ${accountDefinition.uiConfig.name} account`,
      }}
      footer={{
        actionButton: {
          text: 'Save account',
          icon: !isNext && <FontAwesomeIcon icon={regular('floppy-disk')} />,
          onClick: async () => {
            try {
              await form.validateFields();
              setLoading(true);

              const payload = getEditNonOAuthPayload();
              await updateNonOauthAccountApi(workspaceStore.id, account.id, payload);

              await mutate(
                getResourceCacheKey(
                  workspaceStore.id,
                  resourceDefinitionName,
                  ApiGetResource.ACCOUNTS,
                ),
              );

              multiStepModalStore.prev();
            } catch (error) {
              messagesStore.showErrorMessage(
                typeof error === 'string' ? error : 'Failed to update account',
              );
            } finally {
              setLoading(false);
            }
          },
        },
        loading,
      }}
    >
      <LightFormBuilder formConfig={editFormConfig} form={form} variant={variant} />
    </ModalStep>
  );
});
